import { useState } from 'react';
import { apiUrl, formatBytes, type Message } from '@wa/shared';

/**
 * The attachment inside a bubble.
 *
 * Inbound media arrives as an id and the bytes follow a moment later, so there
 * are three states to render, not one: waiting, ready, and never arriving. A
 * spinner that never resolves is worse than saying the attachment is lost.
 */
export function MediaBubble({ message }: { message: Message }) {
  const [expanded, setExpanded] = useState(false);

  // The API hands back a path; the portal is on another origin.
  const src = message.mediaUrl ? apiUrl(message.mediaUrl) : null;

  if (message.mediaStatus === 2) {
    return (
      <div className="flex items-center gap-2 rounded bg-black/5 px-2.5 py-2 text-small text-(--color-muted) dark:bg-white/5">
        <Icon kind="warning" />
        <span>Attachment unavailable — it expired before we could fetch it.</span>
      </div>
    );
  }

  if (!src) {
    return (
      <div className="flex items-center gap-2 rounded bg-black/5 px-2.5 py-2 text-small text-(--color-muted) dark:bg-white/5">
        <span className="h-3.5 w-3.5 shrink-0 animate-pulse rounded-full bg-(--color-muted)" />
        <span>Downloading attachment…</span>
      </div>
    );
  }

  const kind = message.mediaMimeType?.split('/')[0];

  if (kind === 'image') {
    return (
      <>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="block overflow-hidden rounded"
          aria-label="Open image full size"
        >
          {/* A bounded box, so a 4000px photo cannot decide the bubble's width. */}
          <img
            src={src}
            alt={message.mediaFileName ?? 'Received image'}
            loading="lazy"
            className="max-h-72 w-full max-w-sm object-cover"
          />
        </button>

        {expanded && (
          <div
            className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setExpanded(false)}
          >
            <img
              src={src}
              alt={message.mediaFileName ?? 'Image'}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        )}
      </>
    );
  }

  if (kind === 'video') {
    return <video src={src} controls preload="metadata" className="max-h-72 w-full max-w-sm rounded" />;
  }

  if (kind === 'audio') {
    return <audio src={src} controls preload="metadata" className="w-full max-w-xs" />;
  }

  return (
    <a
      href={src}
      download={message.mediaFileName ?? undefined}
      className="tap flex items-center gap-2.5 rounded bg-black/5 px-2.5 py-2 no-underline dark:bg-white/5"
    >
      <Icon kind="document" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-small font-medium text-(--color-ink)">
          {message.mediaFileName ?? 'Document'}
        </span>
        <span className="block text-micro text-(--color-muted)">
          {formatBytes(message.mediaByteSize)}
        </span>
      </span>
    </a>
  );
}

function Icon({ kind }: { kind: 'document' | 'warning' }) {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" className="shrink-0 text-(--color-muted)">
      {kind === 'document' ? (
        <>
          <path d="M5 2.5h6L15.5 7v10.5h-11z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M11 2.5V7h4.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M10 6v4.6M10 13.4v.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}
